import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'utils/i18n';
import ArchitectureNavigation from './ArchitectureNavigation';
import CodeExamples from './CodeExamples';
import { getCurrentArchitecturePage, getParentPageFromPath } from './architecturePagesConfig';

/**
 * ArchitectureLayout
 * Architecture document layout with navigation.
 * Uses DocsLayout as base with architecture-specific customizations.
 * Props:
 * - title: string
 * - lastUpdated: string
 * - sections: Array<{ id, title, content (markdown|string), children?: [], codeExamples?: [] }>
 */
const ArchitectureLayout = ({ title, lastUpdated, sections = [] }) => {
  // Extract code examples from sections
  const codeExamples = useMemo(() => {
    const allExamples = [];
    sections.forEach(section => {
      if (section.codeExamples && Array.isArray(section.codeExamples)) {
        allExamples.push(...section.codeExamples);
      }
    });
    return allExamples;
  }, [sections]);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Get current page and parent page
  // eslint-disable-next-line no-unused-vars
  const { currentPage, parentPage } = useMemo(() => {
    const current = getCurrentArchitecturePage(location.pathname);
    const parent = current ? getParentPageFromPath(location.pathname) : null;
    return { currentPage: current, parentPage: parent };
  }, [location.pathname]);

  // Get language prefix
  const langPrefix = useMemo(() => {
    const match = location.pathname.match(/^\/([a-z]{2})\//);
    return match ? `/${match[1]}` : '';
  }, [location.pathname]);

  const titleStyle = {
    fontSize: 'var(--text-base)',
    fontWeight: '600',
    margin: '0 0 var(--spacing-sm) 0',
    color: 'var(--color-text-primary)'
  };

  const metaStyle = {
    fontSize: 'var(--text-sm)',
    lineHeight: 1.5,
    margin: '0 0 var(--spacing-md) 0',
    color: 'var(--color-text-muted)'
  };

  // Render back button if on a sub-page
  const renderBackButton = () => {
    if (!parentPage) return null;

    const getLabel = (page) => {
      const translated = t(`architecture.${page.translationKey}`, page.label);
      return translated && translated !== page.label ? translated : page.label;
    };

    const handleBackClick = () => {
      const targetPath = langPrefix ? `${langPrefix}${parentPage.path}` : parentPage.path;
      navigate(targetPath);
    };

    const backButtonStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 'var(--spacing-xs)',
      marginBottom: 'var(--spacing-md)',
      padding: 'var(--spacing-xs) var(--spacing-sm)',
      fontSize: 'var(--text-sm)',
      color: 'var(--color-text-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md)',
      background: 'var(--color-bg-primary)',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    };

    return (
      <button
        onClick={handleBackClick}
        style={backButtonStyle}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'var(--color-bg-hover)';
          e.target.style.borderColor = 'var(--color-primary)';
          e.target.style.color = 'var(--color-primary)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'var(--color-bg-primary)';
          e.target.style.borderColor = 'var(--color-border)';
          e.target.style.color = 'var(--color-text-secondary)';
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ transform: 'rotate(180deg)' }}>
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
        </svg>
        {t('architecture.backTo', `Back to ${getLabel(parentPage)}`, { parent: getLabel(parentPage) })}
      </button>
    );
  };

  // Render sidebar top with navigation and title/meta
  const renderSidebarTop = () => (
    <>
      {/* Desktop Vertical Tab Navigation */}
      <ArchitectureNavigation mobileOnly={false} />
      
      {/* Document Title and Meta */}
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <h2 style={titleStyle}>{title}</h2>
        {lastUpdated ? (
          <div style={metaStyle}>Last updated: {lastUpdated}</div>
        ) : null}
      </div>
    </>
  );

  // Render content before sections (back button)
  const renderContentBeforeSections = () => {
    return renderBackButton();
  };

  return (
    <>
      {/* Mobile Dropdown Navigation - At top of page */}
      <div className="docs-navigation-mobile-top">
        <ArchitectureNavigation mobileOnly={true} />
      </div>
      
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: 'var(--spacing-lg)', width: '100%', boxSizing: 'border-box' }}>
        <div className="docs-layout architecture-layout-with-code" style={{ display: 'grid', gridTemplateColumns: codeExamples.length > 0 ? '280px 1fr 420px' : '280px 1fr', gap: 'var(--spacing-lg)', alignItems: 'start' }}>
          {/* Left Sidebar - Navigation */}
          <aside style={{ position: 'sticky', top: '76px', height: 'calc(100vh - 96px)', maxHeight: 'calc(100vh - 96px)', overflowY: 'auto', overflowX: 'hidden', paddingRight: 'var(--spacing-sm)', borderRight: '1px solid var(--color-border-light)', width: '280px', minWidth: '280px', maxWidth: '280px' }}>
            {renderSidebarTop()}
          </aside>
          
          {/* Main Content */}
          <main style={{ paddingBottom: 'var(--spacing-3xl)', flex: 1, minWidth: 0 }}>
            <header style={{ marginBottom: 'var(--spacing-md)' }}>
              <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', marginBottom: '16px', color: 'var(--color-text-primary)' }}>{title}</h1>
              {lastUpdated ? (
                <p style={{ fontSize: 'var(--text-sm)', margin: '0 0 var(--spacing-md) 0', color: 'var(--color-text-muted)' }}>Last updated: {lastUpdated}</p>
              ) : null}
            </header>
            {renderContentBeforeSections()}
            {sections.map((s) => {
              // eslint-disable-next-line no-unused-vars
              const { codeExamples: _, ...sectionWithoutCode } = s;
              return (
                <div key={s.id} style={{ marginBottom: 'var(--spacing-lg)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-light)', backgroundColor: 'var(--color-bg-card)' }}>
                  <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: '600', marginBottom: '12px', marginTop: 0, color: 'var(--color-text-primary)' }}>{s.title}</h2>
                  <div style={{ marginTop: 'var(--spacing-sm)', padding: 'var(--spacing-md)' }}>
                    {Array.isArray(s.content) ? s.content.map((item, i) => {
                      if (React.isValidElement(item)) {
                        return <React.Fragment key={i}>{item}</React.Fragment>;
                      }
                      if (typeof item === 'string') {
                        const isHTML = /<[a-z][\s\S]*>/i.test(item.trim());
                        if (isHTML) {
                          return (
                            <div
                              key={i}
                              dangerouslySetInnerHTML={{ __html: item }}
                              style={{ marginTop: 'var(--spacing-xs)', marginBottom: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}
                            />
                          );
                        }
                        if (item.trim() === '') {
                          return null;
                        }
                        return (
                          <p key={i} style={{ fontSize: 'var(--text-sm)', lineHeight: 1.6, marginTop: 'var(--spacing-xs)', marginBottom: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}>
                            {item}
                          </p>
                        );
                      }
                      return (
                        <p key={i} style={{ fontSize: 'var(--text-sm)', lineHeight: 1.6, marginTop: 'var(--spacing-xs)', marginBottom: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}>
                          {String(item)}
                        </p>
                      );
                    }) : typeof s.content === 'string' && /<[a-z][\s\S]*>/i.test(s.content.trim()) ? (
                      <div dangerouslySetInnerHTML={{ __html: s.content }} style={{ color: 'var(--color-text-primary)' }} />
                    ) : typeof s.content === 'string' ? (
                      <p style={{ fontSize: 'var(--text-sm)', lineHeight: 1.6, marginTop: 'var(--spacing-xs)', marginBottom: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}>{s.content}</p>
                    ) : (
                      <p style={{ fontSize: 'var(--text-sm)', lineHeight: 1.6, marginTop: 'var(--spacing-xs)', marginBottom: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}>{String(s.content || '')}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </main>
          
          {/* Right Sidebar - Code Examples */}
          {codeExamples.length > 0 && <CodeExamples examples={codeExamples} />}
        </div>
      </div>
    </>
  );
};

export default ArchitectureLayout;

